import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { usePlan } from '../../contexts/PlanContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Skeleton, { SKELETON_CSS } from '../shared/Skeleton';

// Vector recreation of the official Google Sheets mark (folded-corner
// document + green grid) — embedded inline on purpose, never fetched from
// an external URL, so this never depends on network access or a
// third-party CDN staying up.
const GoogleSheetsIcon = ({ size = 26, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" style={style} aria-label="Google Sheets">
    <path d="M28 4H12a4 4 0 00-4 4v32a4 4 0 004 4h24a4 4 0 004-4V16L28 4z" fill="#0F9D58" />
    <path d="M28 4l12 12H31a3 3 0 01-3-3V4z" fill="#0B8043" />
    <rect x="13.5" y="20.5" width="21" height="16" rx="1.2" fill="#fff" />
    <path d="M13.5 25h21M13.5 30.5h21M13.5 36h21M20.5 20.5v16M27.5 20.5v16" stroke="#0F9D58" strokeWidth="1.4" />
  </svg>
);

// Real Salesforce cloud logo, served as a static asset (unlike the other
// brand marks on this page, which are embedded inline single-path SVGs) —
// Salesforce's mark has rounded, overlapping cloud shapes that don't
// reduce to a single flat-color path the way those do.
const SalesforceIcon = ({ size = 26, style = {} }) => (
  <img src="/salesforce-logo.png" alt="Salesforce" width={size} height={size * 0.7} style={{ objectFit: 'contain', ...style }} />
);

// Real, single-path HubSpot mark (freely licensed, same open icon set the
// rest of this file's brand marks come from) — embedded inline like
// GoogleSheetsIcon, since HubSpot's sprocket shape reduces cleanly to one
// flat-color path.
const HubSpotIcon = ({ size = 26, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={style} aria-label="HubSpot" fill="#FF7A59">
    <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z" />
  </svg>
);

const LockGlyph = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </svg>
);

// Same visual language already approved for TeamsPanel/Dashboard (Apple-style
// cards, pill buttons, tinted status badges, blurred modal) — reused here so
// Integraciones feels like the same product as the rest of the dashboard.
const IG_CSS = `
  .ig-btn{border:none;border-radius:980px;padding:9px 20px;font-size:13.5px;font-weight:500;cursor:pointer;letter-spacing:-0.01em;transition:opacity .15s ease,background .15s ease;font-family:'Inter',system-ui,sans-serif;white-space:nowrap;display:inline-flex;align-items:center;justify-content:center;}
  .ig-btn-dark{background:#0A0A0A;color:#fff;}
  .ig-btn-dark:hover:not(:disabled){opacity:.86;}
  .ig-btn-ghost{background:rgba(0,0,0,0.045);color:#0A0A0A;}
  .ig-btn-ghost:hover:not(:disabled){background:rgba(0,0,0,0.075);}
  .ig-btn-danger{background:#DC2626;color:#fff;}
  .ig-btn-danger:hover:not(:disabled){opacity:.88;}
  .ig-btn:disabled{opacity:.45;cursor:not-allowed;}
  .ig-badge{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;padding:4px 11px;border-radius:980px;letter-spacing:-0.01em;white-space:nowrap;}
  .ig-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0;}
  .ig-banner{border-radius:12px;padding:11px 14px;font-size:13.5px;line-height:1.5;}
  .ig-row{border-radius:14px;background:#FAFAFA;border:1px solid rgba(0,0,0,0.06);}
  .ig-icon-box{transition:transform .2s ease;}

  @keyframes igFadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  .ig-enter{animation:igFadeUp .3s cubic-bezier(.25,.46,.45,.94) both;}

  .ig-search{display:flex;align-items:center;gap:8px;background:#F5F5F7;border-radius:980px;padding:8px 14px;margin-bottom:24px;width:fit-content;}
  .ig-search input{border:none;background:none;outline:none;font-size:13px;font-family:'Inter',system-ui,sans-serif;color:#0A0A0A;width:170px;}
  .ig-search input::placeholder{color:#9CA3AF;}

  .ig-section{margin-bottom:36px;scroll-margin-top:24px;}
  .ig-section-title{font-size:17px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;margin:0 0 4px;}
  .ig-section-sub{font-size:13px;color:#6B7280;margin:0 0 16px;max-width:520px;}
  .ig-empty{font-size:13px;color:#9CA3AF;margin:0;}

  .ig-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:14px;}
  .ig-tile{display:flex;align-items:center;gap:13px;padding:14px 16px;border-radius:14px;background:#fff;border:1px solid rgba(0,0,0,0.07);text-align:left;font-family:'Inter',system-ui,sans-serif;width:100%;cursor:default;}
  .ig-tile-clickable{cursor:pointer;transition:border-color .2s ease,box-shadow .2s ease,transform .2s ease;}
  .ig-tile-clickable:hover{border-color:rgba(0,0,0,0.14);box-shadow:0 6px 18px rgba(0,0,0,0.07);transform:translateY(-1px);}
  .ig-tile-icon{width:42px;height:42px;border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .ig-tile-body{min-width:0;}
  .ig-tile-name{font-size:13.5px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ig-tile-caption{display:flex;align-items:center;gap:5px;font-size:11.5px;color:#9CA3AF;margin-top:2px;font-weight:500;}
  .ig-tile-caption-locked{color:#B45309;}

  /* Richer card treatment (icon + name/status + description + optional
     last-sync meta + CTA label) — replaces the old compact single-row
     tile so the grid carries real information instead of just a name and
     a dot, which is most of what made this page feel sparse. */
  .ig-card{display:flex;flex-direction:column;gap:14px;padding:20px;border-radius:16px;background:#fff;border:1px solid rgba(0,0,0,0.07);text-align:left;font-family:'Inter',system-ui,sans-serif;width:100%;cursor:default;}
  .ig-card-clickable{cursor:pointer;transition:border-color .2s ease,box-shadow .2s ease,transform .2s ease;}
  .ig-card-clickable:hover{border-color:rgba(0,0,0,0.14);box-shadow:0 8px 22px rgba(0,0,0,0.08);transform:translateY(-1px);}
  .ig-card-head{display:flex;align-items:center;gap:13px;}
  .ig-card-icon{width:44px;height:44px;border-radius:13px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .ig-card-name{font-size:14.5px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;margin-bottom:5px;}
  .ig-card-desc{font-size:12.5px;color:#6B7280;line-height:1.55;margin:0;}
  .ig-card-meta{font-size:11.5px;color:#9CA3AF;padding-top:12px;border-top:1px solid rgba(0,0,0,0.06);}
  .ig-card-cta{font-size:12.5px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;display:flex;align-items:center;gap:4px;}
  .ig-card-cta svg{transition:transform .2s ease;}
  .ig-card-clickable:hover .ig-card-cta svg{transform:translateX(2px);}

  /* "Cómo funciona" steps */
  .ig-steps{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;}
  .ig-step{display:flex;flex-direction:column;gap:10px;}
  .ig-step-num{display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:rgba(0,0,0,0.045);color:#0A0A0A;font-size:12px;font-weight:700;flex-shrink:0;}
  .ig-step-title{font-size:13.5px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;}
  .ig-step-desc{font-size:12.5px;color:#6B7280;line-height:1.55;margin:0;}

  /* Bottom "request an integration" / "developer API" cross-link cards */
  .ig-cta-row{display:grid;grid-template-columns:repeat(2,1fr);gap:14px;}
  .ig-cta-card{display:flex;flex-direction:column;gap:10px;padding:20px;border-radius:16px;background:#FAFAFA;border:1px solid rgba(0,0,0,0.06);}
  .ig-cta-card-icon{width:36px;height:36px;border-radius:11px;background:rgba(0,0,0,0.045);display:flex;align-items:center;justify-content:center;color:#0A0A0A;flex-shrink:0;}
  .ig-cta-card-title{font-size:14px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;}
  .ig-cta-card-desc{font-size:12.5px;color:#6B7280;line-height:1.55;margin:0;flex:1;}
  .ig-cta-card-action{align-self:flex-start;text-decoration:none;}

  .ig-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.32);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:200;padding:20px;}
  .ig-modal{position:relative;background:#fff;border-radius:20px;max-width:480px;width:100%;max-height:88vh;overflow-y:auto;padding:28px;box-shadow:0 20px 60px rgba(0,0,0,0.25);}
  .ig-modal-close{position:absolute;top:16px;right:16px;width:28px;height:28px;border-radius:50%;border:none;background:rgba(0,0,0,0.05);color:#6B7280;font-size:16px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;}
  .ig-modal-close:hover{background:rgba(0,0,0,0.09);}

  @media (max-width: 900px) {
    .ig-steps{grid-template-columns:1fr;}
    .ig-cta-row{grid-template-columns:1fr;}
  }

  @media (max-width: 640px) {
    .ig-grid{grid-template-columns:1fr;}
    .ig-search input{width:120px;}
    .ig-search{padding:7px 12px;}
    .ig-row{padding:12px!important;}
    .ig-row .ig-btn{width:100%;}
    .ig-btn{padding:7px 13px;font-size:11px;min-height:34px;}
    .ig-badge{font-size:10.5px;padding:2.5px 7px;gap:3px;}
    .ig-dot{width:4.5px;height:4.5px;}
    .ig-banner{border-radius:8.5px;padding:8px 9px;font-size:11px;}
    .ig-modal{padding:22px;border-radius:16px;}
    .ig-card{padding:16px;}
    .ig-cta-card{padding:16px;}
  }
`;

const STATUS_META = {
  active: { label: 'Activo', color: '#15803D', bg: 'rgba(22,163,74,0.1)' },
  configuring: { label: 'Configurando', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
  paused: { label: 'Pausado', color: '#B45309', bg: 'rgba(217,119,6,0.1)' },
  requires_attention: { label: 'Requiere atención', color: '#B91C1C', bg: 'rgba(220,38,38,0.1)' },
  disconnected: { label: 'Desconectado', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
};

const statusMetaLabel = (status, t) => t(`integrations.status.${status}`, STATUS_META[status]?.label || status);

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const meta = STATUS_META[status] || STATUS_META.disconnected;
  return (
    <span className="ig-badge" style={{ color: meta.color, background: meta.bg }}>
      <span className="ig-dot" style={{ background: meta.color }} />
      {statusMetaLabel(status, t)}
    </span>
  );
};

const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

// Shared card shape for every provider in the grid — icon + name + status
// badge, a one-line description for context, an optional "last synced"
// meta row for connected integrations, and a CTA label. Replaces the old
// single-row ig-tile (still used for the loading skeleton's shape) so the
// grid carries real information instead of just a name and a status dot.
const IntegrationCard = ({ testId, icon, iconBg, name, description, locked, comingSoon, status, lastSyncLabel, onClick }) => {
  const { t } = useLanguage();
  const clickable = !comingSoon;

  let badge;
  if (locked) {
    badge = (
      <span className="ig-badge" style={{ color: '#B45309', background: 'rgba(217,119,6,0.1)' }}>
        <LockGlyph /> {t('integrations.requiresPaidPlan', 'Requiere plan pago')}
      </span>
    );
  } else if (comingSoon) {
    badge = (
      <span className="ig-badge" style={{ color: '#6B7280', background: 'rgba(107,114,128,0.1)' }}>
        <span className="ig-dot" style={{ background: '#9CA3AF' }} />
        {t('integrations.comingSoon', 'Próximamente')}
      </span>
    );
  } else {
    badge = <StatusBadge status={status} />;
  }

  const Tag = clickable ? 'button' : 'div';

  return (
    <Tag
      type={clickable ? 'button' : undefined}
      className={`ig-card ${clickable ? 'ig-card-clickable' : ''}`}
      onClick={clickable ? onClick : undefined}
      data-testid={testId}
    >
      <div className="ig-card-head">
        <div className="ig-card-icon" style={{ background: iconBg }}>{icon}</div>
        <div className="min-w-0">
          <div className="ig-card-name">{name}</div>
          {badge}
        </div>
      </div>
      <p className="ig-card-desc">{description}</p>
      {lastSyncLabel && <div className="ig-card-meta">{lastSyncLabel}</div>}
      {clickable && !locked && (
        <div className="ig-card-cta">
          {status && status !== 'disconnected' ? t('integrations.manage', 'Gestionar') : t('integrations.connect', 'Conectar')}
          <ArrowIcon />
        </div>
      )}
    </Tag>
  );
};

const ComingSoonTile = ({ testKey, name, icon, iconBg, description }) => {
  const { t } = useLanguage();
  return (
    <IntegrationCard
      testId={`ig-tile-${testKey}`}
      icon={icon}
      iconBg={iconBg}
      name={name}
      description={description || t('integrations.comingSoonDesc', 'Estamos terminando esta integración. Pronto podrás activarla desde aquí.')}
      comingSoon
    />
  );
};

// The only real, functional tile in the grid. Backend remains the final
// authority regardless of what this shows (every Google Sheets endpoint
// re-checks entitlement server-side) — this only decides the click target:
// the upgrade modal for Free accounts, the connect/manage modal otherwise.
const GoogleSheetsTile = ({ canUse, gs, onOpen, onUpgrade }) => {
  const { t, lang } = useLanguage();
  const icon = <GoogleSheetsIcon size={26} />;
  const description = t('integrations.gsheets.desc', 'Envía automáticamente los nuevos contactos de CONTACT a una planilla de Google Sheets.');

  if (!canUse) {
    return (
      <IntegrationCard
        testId="ig-tile-google_sheets" icon={icon} iconBg="#F8F9FA" name="Google Sheets"
        description={description} locked onClick={onUpgrade}
      />
    );
  }

  // While the pilot check is in flight, and once it resolves to "not a
  // pilot" (INTEGRATIONS_ACCESS_MODE="pilot" and this account isn't on the
  // list), this renders exactly like the other coming-soon tiles — it must
  // never reveal that a pilot mechanism exists.
  if (gs.loading || !gs.isPilot) {
    return <ComingSoonTile testKey="google_sheets" name="Google Sheets" icon={icon} iconBg="#F8F9FA" description={description} />;
  }

  const isConnected = gs.connection?.status === 'connected';
  const destStatus = gs.destination?.status;
  const status = isConnected ? (destStatus || 'configuring') : 'disconnected';
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const lastSyncLabel = gs.destination?.last_success_at
    ? `${t('integrations.lastSync', 'Última sincronización:')} ${new Date(gs.destination.last_success_at).toLocaleString(dateLocale)}`
    : null;

  return (
    <IntegrationCard
      testId="ig-tile-google_sheets" icon={icon} iconBg="#F8F9FA" name="Google Sheets"
      description={description} status={status} lastSyncLabel={lastSyncLabel} onClick={onOpen}
    />
  );
};

// Connect/manage surface for Google Sheets, now presented in a modal
// instead of an inline card — the OAuth connect, spreadsheet creation,
// activate/pause/disconnect and test-connection flows are unchanged from
// the previous inline version, just driven by props from the parent
// instead of managing their own fetch.
const GoogleSheetsModal = ({ gs, message, setMessage, onRefetch, onClose }) => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const { connection, destination } = gs;
  const isConnected = connection?.status === 'connected';
  const destStatus = destination?.status;

  const runAction = async (action, successText, errorFallback) => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      await action();
      if (successText) setMessage({ type: 'success', text: successText });
      await onRefetch();
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || errorFallback });
    } finally {
      setBusy(false);
    }
  };

  const handleConnect = async () => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      const { data } = await axios.get(`${API}/integrations/google-sheets/authorize`);
      window.location.href = data.authorize_url;
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || t('integrations.gsheets.connectError', 'No se pudo iniciar la conexión con Google.') });
      setBusy(false);
    }
  };

  const handleCreateSpreadsheet = () => runAction(
    () => axios.post(`${API}/integrations/google-sheets/spreadsheet`),
    t('integrations.gsheets.spreadsheetCreated', 'Planilla creada correctamente.'),
    t('integrations.gsheets.spreadsheetCreateError', 'No se pudo crear la planilla.'),
  );

  const handleActivate = () => runAction(
    () => axios.post(`${API}/integrations/google-sheets/activate`),
    t('integrations.gsheets.activated', 'Integración activada.'),
    t('integrations.gsheets.activateError', 'No se pudo activar la integración.'),
  );

  const handlePause = () => runAction(
    () => axios.post(`${API}/integrations/google-sheets/pause`),
    t('integrations.gsheets.paused', 'Integración pausada.'),
    t('integrations.gsheets.pauseError', 'No se pudo pausar la integración.'),
  );

  const handleDisconnect = () => runAction(
    async () => { await axios.delete(`${API}/integrations/google-sheets`); setConfirmDisconnect(false); },
    t('integrations.gsheets.disconnected', 'Desconectado de Google Sheets.'),
    t('integrations.gsheets.disconnectError', 'No se pudo desconectar.'),
  );

  const handleTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/integrations/google-sheets/test`);
      setTestResult(data);
    } catch (e) {
      setTestResult({ success: false, error_message: e?.response?.data?.detail || t('integrations.gsheets.testConnectionError', 'No se pudo probar la conexión.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ig-modal-overlay" onClick={onClose}>
      <div className="ig-modal" onClick={(e) => e.stopPropagation()} data-testid="integrations-google-sheets">
        <button type="button" className="ig-modal-close" onClick={onClose} aria-label={t('integrations.close', 'Cerrar')}>×</button>

        <div className="flex items-start gap-3.5">
          <div className="ig-icon-box w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: '#F8F9FA' }}>
            <GoogleSheetsIcon size={26} />
          </div>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: '#0A0A0A', letterSpacing: '-0.015em', margin: 0 }}>
              Google Sheets
            </h3>
            <p style={{ fontSize: 13.5, color: '#6B7280', margin: '2px 0 0', maxWidth: 380 }}>
              {t('integrations.gsheets.desc', 'Envía automáticamente los nuevos contactos de CONTACT a una planilla de Google Sheets.')}
            </p>
          </div>
        </div>

        <div className="mt-3">
          <StatusBadge status={isConnected ? (destStatus || 'configuring') : 'disconnected'} />
        </div>

        {!isConnected && (
          <p className="mt-3" style={{ fontSize: 12.5, color: '#9CA3AF', lineHeight: 1.5 }}>
            {t('integrations.gsheets.accessNote', 'CONTACT solo accede a la planilla que crea para tu cuenta al conectar — nunca a otras planillas ni archivos de tu Google Drive.')}
          </p>
        )}

        {message.text && (
          <div
            className="ig-banner mt-4"
            style={{
              background: message.type === 'success' ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)',
              color: message.type === 'success' ? '#15803D' : '#B91C1C',
              border: `1px solid ${message.type === 'success' ? 'rgba(22,163,74,0.18)' : 'rgba(220,38,38,0.18)'}`,
            }}
          >
            {message.text}
          </div>
        )}

        {!isConnected && (
          <div className="ig-row mt-4 p-4 flex flex-wrap items-center justify-between gap-4">
            <p style={{ fontSize: 13.5, color: '#6B7280', margin: 0 }}>
              {t('integrations.gsheets.notConnected', 'Aún no has conectado tu cuenta de Google.')}
            </p>
            <button data-testid="gsheets-connect" onClick={handleConnect} disabled={busy} className="ig-btn ig-btn-dark flex-shrink-0">
              {t('integrations.gsheets.connectWithGoogle', 'Conectar con Google')}
            </button>
          </div>
        )}

        {isConnected && !destination && (
          <div className="ig-row mt-4 p-4 flex flex-wrap items-center justify-between gap-4">
            <p style={{ fontSize: 13.5, color: '#374151', margin: 0 }}>
              {t('integrations.gsheets.connectedAs', 'Conectado como')} <strong style={{ color: '#0A0A0A' }}>{connection.provider_account_email}</strong> {t('integrations.gsheets.noSheetYet', '— aún no tienes una planilla. CONTACT creará una nueva automáticamente.')}
            </p>
            <button data-testid="gsheets-create-spreadsheet" onClick={handleCreateSpreadsheet} disabled={busy} className="ig-btn ig-btn-dark flex-shrink-0">
              {t('integrations.gsheets.createSpreadsheet', 'Crear planilla')}
            </button>
          </div>
        )}

        {isConnected && destination && (
          <div className="mt-4 space-y-3.5">
            <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
              {t('integrations.gsheets.connectedAs', 'Conectado como')} <strong style={{ color: '#0A0A0A' }}>{connection.provider_account_email}</strong>
            </p>

            {destStatus === 'requires_attention' && destination.last_error_message && (
              <div className="ig-banner" style={{ background: 'rgba(220,38,38,0.08)', color: '#B91C1C', border: '1px solid rgba(220,38,38,0.18)' }}>
                {destination.last_error_message}
              </div>
            )}

            <div className="flex flex-wrap gap-2.5">
              {destination.configuration?.spreadsheet_url && (
                <a
                  data-testid="gsheets-open"
                  href={destination.configuration.spreadsheet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ig-btn ig-btn-ghost"
                  style={{ textDecoration: 'none' }}
                >
                  {t('integrations.gsheets.openSpreadsheet', 'Abrir planilla')}
                </a>
              )}

              <button data-testid="gsheets-test" onClick={handleTest} disabled={busy} className="ig-btn ig-btn-ghost">
                {t('integrations.gsheets.testConnection', 'Probar conexión')}
              </button>

              {(destStatus === 'configuring' || destStatus === 'paused' || destStatus === 'requires_attention') && (
                <button data-testid="gsheets-activate" onClick={handleActivate} disabled={busy} className="ig-btn ig-btn-dark">
                  {destStatus === 'paused' ? t('integrations.reactivate', 'Reactivar') : t('integrations.activate', 'Activar')}
                </button>
              )}

              {destStatus === 'active' && (
                <button data-testid="gsheets-pause" onClick={handlePause} disabled={busy} className="ig-btn ig-btn-ghost">
                  {t('integrations.pause', 'Pausar')}
                </button>
              )}
            </div>

            {testResult && (
              <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: testResult.success ? '#15803D' : '#DC2626' }}>
                {testResult.success ? t('integrations.gsheets.testRowSent', 'Fila de prueba enviada correctamente.') : (testResult.error_message || t('integrations.gsheets.testFailed', 'La prueba falló.'))}
              </p>
            )}

            {(destination.last_success_at || destination.last_error_at) && (
              <div className="flex flex-wrap gap-x-5 gap-y-1" style={{ fontSize: 12, color: '#9CA3AF' }}>
                {destination.last_success_at && (
                  <span>{t('integrations.gsheets.lastSync', 'Última sincronización:')} {new Date(destination.last_success_at).toLocaleString(dateLocale)}</span>
                )}
                {destination.last_error_at && (
                  <span>{t('integrations.gsheets.lastError', 'Último error:')} {new Date(destination.last_error_at).toLocaleString(dateLocale)}</span>
                )}
              </div>
            )}
          </div>
        )}

        {isConnected && (
          <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            {!confirmDisconnect ? (
              <button
                data-testid="gsheets-disconnect"
                onClick={() => setConfirmDisconnect(true)}
                style={{ fontSize: 13, fontWeight: 500, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                {t('integrations.gsheets.disconnectBtn', 'Desconectar Google Sheets')}
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span style={{ fontSize: 13, color: '#374151' }}>
                  {t('integrations.gsheets.disconnectConfirm', '¿Desconectar Google Sheets? Dejarás de recibir nuevos contactos ahí.')}
                </span>
                <button
                  data-testid="gsheets-disconnect-confirm"
                  onClick={handleDisconnect}
                  disabled={busy}
                  className="ig-btn ig-btn-danger"
                  style={{ padding: '7px 16px', fontSize: 12.5 }}
                >
                  {busy ? t('integrations.gsheets.disconnecting', 'Desconectando…') : t('integrations.gsheets.yesDisconnect', 'Sí, desconectar')}
                </button>
                <button
                  onClick={() => setConfirmDisconnect(false)}
                  className="ig-btn ig-btn-ghost"
                  style={{ padding: '7px 16px', fontSize: 12.5 }}
                >
                  {t('integrations.cancel', 'Cancelar')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Mirrors GoogleSheetsTile exactly — same canUse/pilot/status contract,
// same click-target decision (upgrade modal vs connect/manage modal).
// Reuses the google_sheets plan-feature gate: both are the same backend
// "integrations" entitlement (see integrations/models.py's
// PLAN_FEATURE_KEY), not two separately-priced features.
const SalesforceTile = ({ canUse, sf, onOpen, onUpgrade }) => {
  const { t, lang } = useLanguage();
  const icon = <SalesforceIcon size={30} />;
  const description = t('integrations.salesforce.desc', 'Envía automáticamente los nuevos contactos de CONTACT como Leads a tu Salesforce.');

  if (!canUse) {
    return (
      <IntegrationCard
        testId="ig-tile-salesforce" icon={icon} iconBg="#F8F9FA" name="Salesforce"
        description={description} locked onClick={onUpgrade}
      />
    );
  }

  if (sf.loading || !sf.isPilot) {
    return <ComingSoonTile testKey="salesforce" name="Salesforce" icon={icon} iconBg="#F8F9FA" description={description} />;
  }

  const isConnected = sf.connection?.status === 'connected';
  const destStatus = sf.destination?.status;
  const status = isConnected ? (destStatus || 'configuring') : 'disconnected';
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const lastSyncLabel = sf.destination?.last_success_at
    ? `${t('integrations.lastSync', 'Última sincronización:')} ${new Date(sf.destination.last_success_at).toLocaleString(dateLocale)}`
    : null;

  return (
    <IntegrationCard
      testId="ig-tile-salesforce" icon={icon} iconBg="#F8F9FA" name="Salesforce"
      description={description} status={status} lastSyncLabel={lastSyncLabel} onClick={onOpen}
    />
  );
};

// Connect/manage surface for Salesforce. Differs from GoogleSheetsModal in
// one structural way: there's no "create a new file" step — the Leads
// object already exists in the connected org — so "crear planilla"
// becomes a single "Conectar sincronización de Leads" step with no input.
const SalesforceModal = ({ sf, message, setMessage, onRefetch, onClose }) => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const { connection, destination } = sf;
  const isConnected = connection?.status === 'connected';
  const destStatus = destination?.status;

  const runAction = async (action, successText, errorFallback) => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      await action();
      if (successText) setMessage({ type: 'success', text: successText });
      await onRefetch();
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || errorFallback });
    } finally {
      setBusy(false);
    }
  };

  const handleConnect = async () => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      const { data } = await axios.get(`${API}/integrations/salesforce/authorize`);
      window.location.href = data.authorize_url;
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || t('integrations.salesforce.connectError', 'No se pudo iniciar la conexión con Salesforce.') });
      setBusy(false);
    }
  };

  const handleCreateDestination = () => runAction(
    () => axios.post(`${API}/integrations/salesforce/destination`),
    t('integrations.salesforce.destinationCreated', 'Sincronización de Leads configurada.'),
    t('integrations.salesforce.destinationCreateError', 'No se pudo configurar la sincronización.'),
  );

  const handleActivate = () => runAction(
    () => axios.post(`${API}/integrations/salesforce/activate`),
    t('integrations.salesforce.activated', 'Integración activada.'),
    t('integrations.salesforce.activateError', 'No se pudo activar la integración.'),
  );

  const handlePause = () => runAction(
    () => axios.post(`${API}/integrations/salesforce/pause`),
    t('integrations.salesforce.paused', 'Integración pausada.'),
    t('integrations.salesforce.pauseError', 'No se pudo pausar la integración.'),
  );

  const handleDisconnect = () => runAction(
    async () => { await axios.delete(`${API}/integrations/salesforce`); setConfirmDisconnect(false); },
    t('integrations.salesforce.disconnected', 'Desconectado de Salesforce.'),
    t('integrations.salesforce.disconnectError', 'No se pudo desconectar.'),
  );

  const handleTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/integrations/salesforce/test`);
      setTestResult(data);
    } catch (e) {
      setTestResult({ success: false, error_message: e?.response?.data?.detail || t('integrations.salesforce.testConnectionError', 'No se pudo probar la conexión.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ig-modal-overlay" onClick={onClose}>
      <div className="ig-modal" onClick={(e) => e.stopPropagation()} data-testid="integrations-salesforce">
        <button type="button" className="ig-modal-close" onClick={onClose} aria-label={t('integrations.close', 'Cerrar')}>×</button>

        <div className="flex items-start gap-3.5">
          <div className="ig-icon-box w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: '#F8F9FA' }}>
            <SalesforceIcon size={30} />
          </div>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: '#0A0A0A', letterSpacing: '-0.015em', margin: 0 }}>
              Salesforce
            </h3>
            <p style={{ fontSize: 13.5, color: '#6B7280', margin: '2px 0 0', maxWidth: 380 }}>
              {t('integrations.salesforce.desc', 'Envía automáticamente los nuevos contactos de CONTACT como Leads a tu Salesforce.')}
            </p>
          </div>
        </div>

        <div className="mt-3">
          <StatusBadge status={isConnected ? (destStatus || 'configuring') : 'disconnected'} />
        </div>

        {!isConnected && (
          <p className="mt-3" style={{ fontSize: 12.5, color: '#9CA3AF', lineHeight: 1.5 }}>
            {t('integrations.salesforce.accessNote', 'Necesitas una cuenta de Salesforce con acceso a la API habilitado (incluido en Enterprise/Unlimited/Performance; Professional lo requiere como complemento pago).')}
          </p>
        )}

        {message.text && (
          <div
            className="ig-banner mt-4"
            style={{
              background: message.type === 'success' ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)',
              color: message.type === 'success' ? '#15803D' : '#B91C1C',
              border: `1px solid ${message.type === 'success' ? 'rgba(22,163,74,0.18)' : 'rgba(220,38,38,0.18)'}`,
            }}
          >
            {message.text}
          </div>
        )}

        {!isConnected && (
          <div className="ig-row mt-4 p-4 flex flex-wrap items-center justify-between gap-4">
            <p style={{ fontSize: 13.5, color: '#6B7280', margin: 0 }}>
              {t('integrations.salesforce.notConnected', 'Aún no has conectado tu cuenta de Salesforce.')}
            </p>
            <button data-testid="salesforce-connect" onClick={handleConnect} disabled={busy} className="ig-btn ig-btn-dark flex-shrink-0">
              {t('integrations.salesforce.connectWithSalesforce', 'Conectar con Salesforce')}
            </button>
          </div>
        )}

        {isConnected && !destination && (
          <div className="ig-row mt-4 p-4 flex flex-wrap items-center justify-between gap-4">
            <p style={{ fontSize: 13.5, color: '#374151', margin: 0 }}>
              {t('integrations.salesforce.connectedAs', 'Conectado como')} <strong style={{ color: '#0A0A0A' }}>{connection.provider_account_email || connection.provider_account_name}</strong> {t('integrations.salesforce.noDestinationYet', '— falta activar la sincronización de Leads.')}
            </p>
            <button data-testid="salesforce-create-destination" onClick={handleCreateDestination} disabled={busy} className="ig-btn ig-btn-dark flex-shrink-0">
              {t('integrations.salesforce.setupLeadSync', 'Configurar sincronización')}
            </button>
          </div>
        )}

        {isConnected && destination && (
          <div className="mt-4 space-y-3.5">
            <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
              {t('integrations.salesforce.connectedAs', 'Conectado como')} <strong style={{ color: '#0A0A0A' }}>{connection.provider_account_email || connection.provider_account_name}</strong>
            </p>

            {destStatus === 'requires_attention' && destination.last_error_message && (
              <div className="ig-banner" style={{ background: 'rgba(220,38,38,0.08)', color: '#B91C1C', border: '1px solid rgba(220,38,38,0.18)' }}>
                {destination.last_error_message}
              </div>
            )}

            <div className="flex flex-wrap gap-2.5">
              <button data-testid="salesforce-test" onClick={handleTest} disabled={busy} className="ig-btn ig-btn-ghost">
                {t('integrations.salesforce.testConnection', 'Probar conexión')}
              </button>

              {(destStatus === 'configuring' || destStatus === 'paused' || destStatus === 'requires_attention') && (
                <button data-testid="salesforce-activate" onClick={handleActivate} disabled={busy} className="ig-btn ig-btn-dark">
                  {destStatus === 'paused' ? t('integrations.reactivate', 'Reactivar') : t('integrations.activate', 'Activar')}
                </button>
              )}

              {destStatus === 'active' && (
                <button data-testid="salesforce-pause" onClick={handlePause} disabled={busy} className="ig-btn ig-btn-ghost">
                  {t('integrations.pause', 'Pausar')}
                </button>
              )}
            </div>

            {testResult && (
              <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: testResult.success ? '#15803D' : '#DC2626' }}>
                {testResult.success ? t('integrations.salesforce.testLeadSent', 'Lead de prueba creado correctamente en Salesforce.') : (testResult.error_message || t('integrations.salesforce.testFailed', 'La prueba falló.'))}
              </p>
            )}

            {(destination.last_success_at || destination.last_error_at) && (
              <div className="flex flex-wrap gap-x-5 gap-y-1" style={{ fontSize: 12, color: '#9CA3AF' }}>
                {destination.last_success_at && (
                  <span>{t('integrations.salesforce.lastSync', 'Última sincronización:')} {new Date(destination.last_success_at).toLocaleString(dateLocale)}</span>
                )}
                {destination.last_error_at && (
                  <span>{t('integrations.salesforce.lastError', 'Último error:')} {new Date(destination.last_error_at).toLocaleString(dateLocale)}</span>
                )}
              </div>
            )}
          </div>
        )}

        {isConnected && (
          <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            {!confirmDisconnect ? (
              <button
                data-testid="salesforce-disconnect"
                onClick={() => setConfirmDisconnect(true)}
                style={{ fontSize: 13, fontWeight: 500, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                {t('integrations.salesforce.disconnectBtn', 'Desconectar Salesforce')}
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span style={{ fontSize: 13, color: '#374151' }}>
                  {t('integrations.salesforce.disconnectConfirm', '¿Desconectar Salesforce? Dejarás de enviar nuevos Leads ahí.')}
                </span>
                <button
                  data-testid="salesforce-disconnect-confirm"
                  onClick={handleDisconnect}
                  disabled={busy}
                  className="ig-btn ig-btn-danger"
                  style={{ padding: '7px 16px', fontSize: 12.5 }}
                >
                  {busy ? t('integrations.salesforce.disconnecting', 'Desconectando…') : t('integrations.salesforce.yesDisconnect', 'Sí, desconectar')}
                </button>
                <button
                  onClick={() => setConfirmDisconnect(false)}
                  className="ig-btn ig-btn-ghost"
                  style={{ padding: '7px 16px', fontSize: 12.5 }}
                >
                  {t('integrations.cancel', 'Cancelar')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Mirrors SalesforceTile/GoogleSheetsTile exactly — same canUse/pilot/status
// contract, same click-target decision. Reuses the google_sheets plan-feature
// gate: all three providers share the same backend "integrations" entitlement
// (see integrations/models.py's PLAN_FEATURE_KEY), not three separately-priced
// features.
const HubSpotTile = ({ canUse, hs, onOpen, onUpgrade }) => {
  const { t, lang } = useLanguage();
  const icon = <HubSpotIcon size={26} />;
  const description = t('integrations.hubspot.desc', 'Envía automáticamente los nuevos contactos de CONTACT como Contactos a tu HubSpot.');

  if (!canUse) {
    return (
      <IntegrationCard
        testId="ig-tile-hubspot" icon={icon} iconBg="#FFF1EB" name="HubSpot"
        description={description} locked onClick={onUpgrade}
      />
    );
  }

  if (hs.loading || !hs.isPilot) {
    return <ComingSoonTile testKey="hubspot" name="HubSpot" icon={icon} iconBg="#FFF1EB" description={description} />;
  }

  const isConnected = hs.connection?.status === 'connected';
  const destStatus = hs.destination?.status;
  const status = isConnected ? (destStatus || 'configuring') : 'disconnected';
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const lastSyncLabel = hs.destination?.last_success_at
    ? `${t('integrations.lastSync', 'Última sincronización:')} ${new Date(hs.destination.last_success_at).toLocaleString(dateLocale)}`
    : null;

  return (
    <IntegrationCard
      testId="ig-tile-hubspot" icon={icon} iconBg="#FFF1EB" name="HubSpot"
      description={description} status={status} lastSyncLabel={lastSyncLabel} onClick={onOpen}
    />
  );
};

// Connect/manage surface for HubSpot. Same single-step shape as
// SalesforceModal (no "create a new file" step — the Contacts object
// already exists in the connected portal).
const HubSpotModal = ({ hs, message, setMessage, onRefetch, onClose }) => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const { connection, destination } = hs;
  const isConnected = connection?.status === 'connected';
  const destStatus = destination?.status;

  const runAction = async (action, successText, errorFallback) => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      await action();
      if (successText) setMessage({ type: 'success', text: successText });
      await onRefetch();
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || errorFallback });
    } finally {
      setBusy(false);
    }
  };

  const handleConnect = async () => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      const { data } = await axios.get(`${API}/integrations/hubspot/authorize`);
      window.location.href = data.authorize_url;
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || t('integrations.hubspot.connectError', 'No se pudo iniciar la conexión con HubSpot.') });
      setBusy(false);
    }
  };

  const handleCreateDestination = () => runAction(
    () => axios.post(`${API}/integrations/hubspot/destination`),
    t('integrations.hubspot.destinationCreated', 'Sincronización de Contactos configurada.'),
    t('integrations.hubspot.destinationCreateError', 'No se pudo configurar la sincronización.'),
  );

  const handleActivate = () => runAction(
    () => axios.post(`${API}/integrations/hubspot/activate`),
    t('integrations.hubspot.activated', 'Integración activada.'),
    t('integrations.hubspot.activateError', 'No se pudo activar la integración.'),
  );

  const handlePause = () => runAction(
    () => axios.post(`${API}/integrations/hubspot/pause`),
    t('integrations.hubspot.paused', 'Integración pausada.'),
    t('integrations.hubspot.pauseError', 'No se pudo pausar la integración.'),
  );

  const handleDisconnect = () => runAction(
    async () => { await axios.delete(`${API}/integrations/hubspot`); setConfirmDisconnect(false); },
    t('integrations.hubspot.disconnected', 'Desconectado de HubSpot.'),
    t('integrations.hubspot.disconnectError', 'No se pudo desconectar.'),
  );

  const handleTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/integrations/hubspot/test`);
      setTestResult(data);
    } catch (e) {
      setTestResult({ success: false, error_message: e?.response?.data?.detail || t('integrations.hubspot.testConnectionError', 'No se pudo probar la conexión.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ig-modal-overlay" onClick={onClose}>
      <div className="ig-modal" onClick={(e) => e.stopPropagation()} data-testid="integrations-hubspot">
        <button type="button" className="ig-modal-close" onClick={onClose} aria-label={t('integrations.close', 'Cerrar')}>×</button>

        <div className="flex items-start gap-3.5">
          <div className="ig-icon-box w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: '#FFF1EB' }}>
            <HubSpotIcon size={26} />
          </div>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: '#0A0A0A', letterSpacing: '-0.015em', margin: 0 }}>
              HubSpot
            </h3>
            <p style={{ fontSize: 13.5, color: '#6B7280', margin: '2px 0 0', maxWidth: 380 }}>
              {t('integrations.hubspot.desc', 'Envía automáticamente los nuevos contactos de CONTACT como Contactos a tu HubSpot.')}
            </p>
          </div>
        </div>

        <div className="mt-3">
          <StatusBadge status={isConnected ? (destStatus || 'configuring') : 'disconnected'} />
        </div>

        {!isConnected && (
          <p className="mt-3" style={{ fontSize: 12.5, color: '#9CA3AF', lineHeight: 1.5 }}>
            {t('integrations.hubspot.accessNote', 'Funciona con cualquier cuenta de HubSpot, incluyendo el CRM gratuito.')}
          </p>
        )}

        {message.text && (
          <div
            className="ig-banner mt-4"
            style={{
              background: message.type === 'success' ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)',
              color: message.type === 'success' ? '#15803D' : '#B91C1C',
              border: `1px solid ${message.type === 'success' ? 'rgba(22,163,74,0.18)' : 'rgba(220,38,38,0.18)'}`,
            }}
          >
            {message.text}
          </div>
        )}

        {!isConnected && (
          <div className="ig-row mt-4 p-4 flex flex-wrap items-center justify-between gap-4">
            <p style={{ fontSize: 13.5, color: '#6B7280', margin: 0 }}>
              {t('integrations.hubspot.notConnected', 'Aún no has conectado tu cuenta de HubSpot.')}
            </p>
            <button data-testid="hubspot-connect" onClick={handleConnect} disabled={busy} className="ig-btn ig-btn-dark flex-shrink-0">
              {t('integrations.hubspot.connectWithHubSpot', 'Conectar con HubSpot')}
            </button>
          </div>
        )}

        {isConnected && !destination && (
          <div className="ig-row mt-4 p-4 flex flex-wrap items-center justify-between gap-4">
            <p style={{ fontSize: 13.5, color: '#374151', margin: 0 }}>
              {t('integrations.hubspot.connectedAs', 'Conectado como')} <strong style={{ color: '#0A0A0A' }}>{connection.provider_account_email || connection.provider_account_name}</strong> {t('integrations.hubspot.noDestinationYet', '— falta activar la sincronización de Contactos.')}
            </p>
            <button data-testid="hubspot-create-destination" onClick={handleCreateDestination} disabled={busy} className="ig-btn ig-btn-dark flex-shrink-0">
              {t('integrations.hubspot.setupLeadSync', 'Configurar sincronización')}
            </button>
          </div>
        )}

        {isConnected && destination && (
          <div className="mt-4 space-y-3.5">
            <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
              {t('integrations.hubspot.connectedAs', 'Conectado como')} <strong style={{ color: '#0A0A0A' }}>{connection.provider_account_email || connection.provider_account_name}</strong>
            </p>

            {destStatus === 'requires_attention' && destination.last_error_message && (
              <div className="ig-banner" style={{ background: 'rgba(220,38,38,0.08)', color: '#B91C1C', border: '1px solid rgba(220,38,38,0.18)' }}>
                {destination.last_error_message}
              </div>
            )}

            <div className="flex flex-wrap gap-2.5">
              <button data-testid="hubspot-test" onClick={handleTest} disabled={busy} className="ig-btn ig-btn-ghost">
                {t('integrations.hubspot.testConnection', 'Probar conexión')}
              </button>

              {(destStatus === 'configuring' || destStatus === 'paused' || destStatus === 'requires_attention') && (
                <button data-testid="hubspot-activate" onClick={handleActivate} disabled={busy} className="ig-btn ig-btn-dark">
                  {destStatus === 'paused' ? t('integrations.reactivate', 'Reactivar') : t('integrations.activate', 'Activar')}
                </button>
              )}

              {destStatus === 'active' && (
                <button data-testid="hubspot-pause" onClick={handlePause} disabled={busy} className="ig-btn ig-btn-ghost">
                  {t('integrations.pause', 'Pausar')}
                </button>
              )}
            </div>

            {testResult && (
              <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: testResult.success ? '#15803D' : '#DC2626' }}>
                {testResult.success ? t('integrations.hubspot.testContactSent', 'Contacto de prueba creado correctamente en HubSpot.') : (testResult.error_message || t('integrations.hubspot.testFailed', 'La prueba falló.'))}
              </p>
            )}

            {(destination.last_success_at || destination.last_error_at) && (
              <div className="flex flex-wrap gap-x-5 gap-y-1" style={{ fontSize: 12, color: '#9CA3AF' }}>
                {destination.last_success_at && (
                  <span>{t('integrations.hubspot.lastSync', 'Última sincronización:')} {new Date(destination.last_success_at).toLocaleString(dateLocale)}</span>
                )}
                {destination.last_error_at && (
                  <span>{t('integrations.hubspot.lastError', 'Último error:')} {new Date(destination.last_error_at).toLocaleString(dateLocale)}</span>
                )}
              </div>
            )}
          </div>
        )}

        {isConnected && (
          <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            {!confirmDisconnect ? (
              <button
                data-testid="hubspot-disconnect"
                onClick={() => setConfirmDisconnect(true)}
                style={{ fontSize: 13, fontWeight: 500, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                {t('integrations.hubspot.disconnectBtn', 'Desconectar HubSpot')}
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span style={{ fontSize: 13, color: '#374151' }}>
                  {t('integrations.hubspot.disconnectConfirm', '¿Desconectar HubSpot? Dejarás de enviar nuevos Contactos ahí.')}
                </span>
                <button
                  data-testid="hubspot-disconnect-confirm"
                  onClick={handleDisconnect}
                  disabled={busy}
                  className="ig-btn ig-btn-danger"
                  style={{ padding: '7px 16px', fontSize: 12.5 }}
                >
                  {busy ? t('integrations.hubspot.disconnecting', 'Desconectando…') : t('integrations.hubspot.yesDisconnect', 'Sí, desconectar')}
                </button>
                <button
                  onClick={() => setConfirmDisconnect(false)}
                  className="ig-btn ig-btn-ghost"
                  style={{ padding: '7px 16px', fontSize: 12.5 }}
                >
                  {t('integrations.cancel', 'Cancelar')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Rendered while auth/plan haven't resolved yet — reuses the shared shimmer
// Skeleton component (same one used elsewhere in the dashboard) instead of
// a one-off gray-block placeholder.
const IntegrationsSkeleton = () => (
  <div className="max-w-4xl" data-testid="integrations-loading">
    <style>{SKELETON_CSS}{IG_CSS}</style>
    <Skeleton width={190} height={26} radius={8} style={{ marginBottom: 10 }} />
    <Skeleton width={360} height={14} radius={6} style={{ marginBottom: 28 }} />
    <Skeleton width={140} height={18} radius={6} style={{ marginBottom: 14 }} />
    <div className="ig-grid" style={{ marginBottom: 32 }}>
      {[0, 1].map((i) => (
        <div key={i} className="ig-card">
          <div className="flex items-center gap-3">
            <Skeleton width={44} height={44} radius={13} />
            <div className="flex-1">
              <Skeleton width={110} height={13} radius={6} style={{ marginBottom: 8 }} />
              <Skeleton width={90} height={16} radius={980} />
            </div>
          </div>
          <Skeleton width="100%" height={12} radius={6} style={{ marginTop: 14 }} />
          <Skeleton width="70%" height={12} radius={6} style={{ marginTop: 6 }} />
        </div>
      ))}
    </div>
  </div>
);

const IntegrationsPanel = ({ setActiveTab } = {}) => {
  // Backend remains the final authority on every real action (OAuth,
  // spreadsheet creation, pause/reactivate/disconnect all re-check
  // entitlement server-side regardless of what renders here) — this only
  // decides what the UI shows.
  const { loading: authLoading } = useAuth();
  const { hasFeature, loading: planLoading, openUpgrade } = usePlan();
  const { t } = useLanguage();

  const [search, setSearch] = useState('');
  const [gsModalOpen, setGsModalOpen] = useState(false);
  const [gs, setGs] = useState({ loading: true, isPilot: true, connection: null, destination: null });
  const [gsMessage, setGsMessage] = useState({ type: '', text: '' });

  const [sfModalOpen, setSfModalOpen] = useState(false);
  const [sf, setSf] = useState({ loading: true, isPilot: true, connection: null, destination: null });
  const [sfMessage, setSfMessage] = useState({ type: '', text: '' });

  const [hsModalOpen, setHsModalOpen] = useState(false);
  const [hs, setHs] = useState({ loading: true, isPilot: true, connection: null, destination: null });
  const [hsMessage, setHsMessage] = useState({ type: '', text: '' });

  const canGSheets = hasFeature('google_sheets');
  // Same underlying "integrations" plan entitlement as Google Sheets — see
  // integrations/models.py's PLAN_FEATURE_KEY (one shared gate, not a
  // separately-priced feature per provider).
  const canSalesforce = hasFeature('google_sheets');
  const canHubSpot = hasFeature('google_sheets');

  const fetchGs = async () => {
    try {
      const { data } = await axios.get(`${API}/integrations/google-sheets/connection`);
      setGs({ loading: false, isPilot: true, connection: data.connection, destination: (data.destinations || [])[0] || null });
    } catch (e) {
      if (e?.response?.status === 404) {
        // Not a pilot account (or the feature isn't enabled at all) — the
        // tile falls back to the same "Próximamente" look as everything
        // else, see GoogleSheetsTile.
        setGs({ loading: false, isPilot: false, connection: null, destination: null });
      } else {
        setGs((prev) => ({ ...prev, loading: false, connection: null, destination: null }));
      }
    }
  };

  const fetchSf = async () => {
    try {
      const { data } = await axios.get(`${API}/integrations/salesforce/connection`);
      setSf({ loading: false, isPilot: true, connection: data.connection, destination: (data.destinations || [])[0] || null });
    } catch (e) {
      if (e?.response?.status === 404) {
        setSf({ loading: false, isPilot: false, connection: null, destination: null });
      } else {
        setSf((prev) => ({ ...prev, loading: false, connection: null, destination: null }));
      }
    }
  };

  const fetchHs = async () => {
    try {
      const { data } = await axios.get(`${API}/integrations/hubspot/connection`);
      setHs({ loading: false, isPilot: true, connection: data.connection, destination: (data.destinations || [])[0] || null });
    } catch (e) {
      if (e?.response?.status === 404) {
        setHs({ loading: false, isPilot: false, connection: null, destination: null });
      } else {
        setHs((prev) => ({ ...prev, loading: false, connection: null, destination: null }));
      }
    }
  };

  useEffect(() => {
    if (!canGSheets || authLoading || planLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('integrations') === 'google_sheets_connected') {
      setGsMessage({ type: 'success', text: t('integrations.gsheets.accountConnected', 'Tu cuenta de Google quedó conectada.') });
      setGsModalOpen(true);
      params.delete('integrations');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
    fetchGs();
  }, [canGSheets, authLoading, planLoading]);

  useEffect(() => {
    if (!canSalesforce || authLoading || planLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('integrations') === 'salesforce_connected') {
      setSfMessage({ type: 'success', text: t('integrations.salesforce.accountConnected', 'Tu cuenta de Salesforce quedó conectada.') });
      setSfModalOpen(true);
      params.delete('integrations');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
    fetchSf();
  }, [canSalesforce, authLoading, planLoading]);

  useEffect(() => {
    if (!canHubSpot || authLoading || planLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('integrations') === 'hubspot_connected') {
      setHsMessage({ type: 'success', text: t('integrations.hubspot.accountConnected', 'Tu cuenta de HubSpot quedó conectada.') });
      setHsModalOpen(true);
      params.delete('integrations');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
    fetchHs();
  }, [canHubSpot, authLoading, planLoading]);

  if (authLoading || planLoading) {
    return <IntegrationsSkeleton />;
  }

  const q = search.trim().toLowerCase();
  const matches = (name) => !q || name.toLowerCase().includes(q);

  const gsVisible = matches('Google Sheets');
  const sfVisible = matches('Salesforce');
  const hsVisible = matches('HubSpot');

  return (
    <div className="max-w-4xl" data-testid="integrations-panel">
      <style>{IG_CSS}</style>

      <div className="mb-6">
        <h2 style={{ fontSize: 26, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.01em', margin: 0, lineHeight: 1.15 }}>
          {t('integrations.title', 'Integraciones')}
        </h2>
        <p style={{ fontSize: 14, color: '#6B7280', marginTop: 8, maxWidth: 520 }}>
          {t('integrations.subtitle', 'Conecta CONTACT con las herramientas que utiliza tu equipo y automatiza el flujo de nuevos contactos.')}
        </p>
      </div>

      <div className="ig-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('integrations.searchPlaceholder', 'Buscar integraciones')}
          data-testid="integrations-search"
        />
      </div>

      {!(gsVisible || sfVisible || hsVisible) && (
        <p className="ig-empty mb-6">{t('integrations.noMatch', 'Ninguna integración coincide con "{query}".').replace('{query}', search)}</p>
      )}

      {(sfVisible || hsVisible) && (
        <section className="ig-section">
          <h3 className="ig-section-title">{t('integrations.crm.title', 'CRM y Ventas')}</h3>
          <p className="ig-section-sub">{t('integrations.crm.sub', 'Envía cada nuevo contacto directo a tu CRM, como un Lead o Contacto nuevo.')}</p>
          <div className="ig-grid">
            {sfVisible && (
              <SalesforceTile
                canUse={canSalesforce}
                sf={sf}
                onOpen={() => setSfModalOpen(true)}
                onUpgrade={() => openUpgrade('google_sheets')}
              />
            )}
            {hsVisible && (
              <HubSpotTile
                canUse={canHubSpot}
                hs={hs}
                onOpen={() => setHsModalOpen(true)}
                onUpgrade={() => openUpgrade('google_sheets')}
              />
            )}
          </div>
        </section>
      )}

      {gsVisible && (
        <section className="ig-section">
          <h3 className="ig-section-title">{t('integrations.productivity.title', 'Productividad')}</h3>
          <p className="ig-section-sub">{t('integrations.productivity.sub', 'Mantén tus contactos sincronizados con las herramientas donde ya trabaja tu equipo.')}</p>
          <div className="ig-grid">
            <GoogleSheetsTile
              canUse={canGSheets}
              gs={gs}
              onOpen={() => setGsModalOpen(true)}
              onUpgrade={() => openUpgrade('google_sheets')}
            />
          </div>
        </section>
      )}

      <section className="ig-section">
        <h3 className="ig-section-title">{t('integrations.howItWorks.title', 'Cómo funciona')}</h3>
        <p className="ig-section-sub">{t('integrations.howItWorks.sub', 'El mismo flujo para cualquier integración, de principio a fin.')}</p>
        <div className="ig-steps">
          <div className="ig-step">
            <span className="ig-step-num">1</span>
            <div className="ig-step-title">{t('integrations.howItWorks.step1Title', 'Conecta tu cuenta')}</div>
            <p className="ig-step-desc">{t('integrations.howItWorks.step1Desc', 'Autorizas el acceso con un clic. Nunca compartes tu contraseña con CONTACT.')}</p>
          </div>
          <div className="ig-step">
            <span className="ig-step-num">2</span>
            <div className="ig-step-title">{t('integrations.howItWorks.step2Title', 'Activa la sincronización')}</div>
            <p className="ig-step-desc">{t('integrations.howItWorks.step2Desc', 'Elige el destino (una planilla, tus Leads, tus Contactos) y actívalo.')}</p>
          </div>
          <div className="ig-step">
            <span className="ig-step-num">3</span>
            <div className="ig-step-title">{t('integrations.howItWorks.step3Title', 'Listo')}</div>
            <p className="ig-step-desc">{t('integrations.howItWorks.step3Desc', 'Cada nuevo contacto se envía solo, sin que tengas que hacer nada más.')}</p>
          </div>
        </div>
      </section>

      <section className="ig-section" style={{ marginBottom: 12 }}>
        <div className="ig-cta-row">
          <div className="ig-cta-card">
            <div className="ig-cta-card-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="ig-cta-card-title">{t('integrations.request.title', '¿Usas otra herramienta?')}</div>
            <p className="ig-cta-card-desc">{t('integrations.request.desc', 'Cuéntanos cuál y la evaluamos para agregarla a la lista.')}</p>
            <a
              href="mailto:soporte@contactnetwork.cl?subject=Solicitud%20de%20integraci%C3%B3n"
              className="ig-btn ig-btn-ghost ig-cta-card-action"
              data-testid="integrations-request"
            >
              {t('integrations.request.action', 'Escribir a soporte')}
            </a>
          </div>

          <div className="ig-cta-card">
            <div className="ig-cta-card-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
              </svg>
            </div>
            <div className="ig-cta-card-title">{t('integrations.dev.title', '¿Prefieres construir la tuya?')}</div>
            <p className="ig-cta-card-desc">{t('integrations.dev.desc', 'Usa nuestra API REST y Webhooks para conectar CONTACT con cualquier sistema.')}</p>
            <button
              type="button"
              className="ig-btn ig-btn-ghost ig-cta-card-action"
              data-testid="integrations-developer-link"
              onClick={() => setActiveTab?.('developer')}
              disabled={!setActiveTab}
            >
              {t('integrations.dev.action', 'Ir al Panel Desarrollador')}
            </button>
          </div>
        </div>
      </section>

      {gsModalOpen && (
        <GoogleSheetsModal
          gs={gs}
          message={gsMessage}
          setMessage={setGsMessage}
          onRefetch={fetchGs}
          onClose={() => setGsModalOpen(false)}
        />
      )}

      {sfModalOpen && (
        <SalesforceModal
          sf={sf}
          message={sfMessage}
          setMessage={setSfMessage}
          onRefetch={fetchSf}
          onClose={() => setSfModalOpen(false)}
        />
      )}

      {hsModalOpen && (
        <HubSpotModal
          hs={hs}
          message={hsMessage}
          setMessage={setHsMessage}
          onRefetch={fetchHs}
          onClose={() => setHsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default IntegrationsPanel;
